import QRCode from 'qrcode';
import { useEffect, useRef, useState } from 'react';
import { phoneApi, type PairedPhone } from '../phone-sync.js';
import { tripApi } from '../trip-api.js';
import type { LinkedBrowser } from '../../shared/trip-api.js';
import type { PhonesState } from '../usePhones.js';
import { track } from '../telemetry.js';
import { Button } from './ui.js';

type Pairing = { status: 'none' } | { status: 'loading' } | { status: 'ready'; url: string; svg: string; expiresAt: Date } | { status: 'failed'; message: string };

const time = (iso: string) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/**
 * "Phone" (#35, #51): pair the ride app with this planner by scanning a code.
 * Pairing puts this browser's trips and the phone's together; after that both
 * show and edit the same trips. Lists the paired phones and browsers.
 */
export function PhoneDialog({ plannerKey, phones: state, tripName, onClose, onPaired, beforePair }: {
  plannerKey: string;
  /** The planner's name for a trip id. */
  tripName: (tripId: string) => string;
  /** Kept current by the page's event stream (`usePhones`). */
  phones: PhonesState & { refresh: () => Promise<PairedPhone[] | undefined> };
  onPaired: () => void;
  beforePair: () => Promise<unknown>;
  onClose: () => void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const [api] = useState(() => phoneApi(plannerKey));
  const [tripsApi] = useState(() => tripApi(plannerKey));
  const [browsers, setBrowsers] = useState<LinkedBrowser[]>();
  const [browserError, setBrowserError] = useState<string>();
  const paired = useRef(onPaired);
  useEffect(() => { paired.current = onPaired; }, [onPaired]);
  const { phones, error: listError, live, refresh } = state;
  const [unpairError, setUnpairError] = useState<string>();
  const [pairing, setPairing] = useState<Pairing>({ status: 'none' });
  const [justPaired, setJustPaired] = useState(false);

  useEffect(() => {
    let disposed = false;
    void tripsApi.browsers().then(({ browsers }) => { if (!disposed) setBrowsers(browsers); }).catch((error: unknown) => { if (!disposed) setBrowserError(error instanceof Error ? error.message : String(error)); });
    return () => { disposed = true; };
  }, [tripsApi]);

  // While a code shows, wait for a phone to redeem it: this browser may then have moved to the phone's trips.
  useEffect(() => {
    if (pairing.status !== 'ready') return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const state = await api.pairingState();
        if (disposed) return;
        if (state === 'paired') {
          track('phone-paired');
          setPairing({ status: 'none' }); setJustPaired(true);
          paired.current();
          void refresh();
          setBrowsers((await tripsApi.browsers()).browsers);
          return;
        }
        if (state === 'expired' || state === 'none') { setPairing({ status: 'failed', message: 'The code expired. Make a new one.' }); return; }
      } catch { /* Keep the code visible and retry while it is valid. */ }
      if (!disposed) timer = setTimeout(() => void poll(), 2000);
    };
    timer = setTimeout(() => void poll(), 2000);
    return () => { disposed = true; clearTimeout(timer); };
  }, [pairing.status, api, tripsApi, refresh]);

  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (pairing.status !== 'ready') return;
    const timer = setTimeout(() => setPairing({ status: 'failed', message: 'The code expired. Make a new one.' }), Math.max(0, pairing.expiresAt.getTime() - Date.now()));
    return () => clearTimeout(timer);
  }, [pairing]);

  const pair = async () => {
    setPairing({ status: 'loading' });
    setJustPaired(false);
    try {
      // Save the open trip before this browser's trips can join the phone's.
      await beforePair();
      const { url, expiresAt } = await api.pairing();
      const svg = await QRCode.toString(url, { type: 'svg', errorCorrectionLevel: 'M', margin: 2 });
      setPairing({ status: 'ready', url, svg, expiresAt: new Date(expiresAt) });
    } catch (error) {
      setPairing({ status: 'failed', message: error instanceof Error ? error.message : String(error) });
    }
  };

  const removeBrowser = async (browser: LinkedBrowser) => {
    if (!window.confirm('Remove this browser? It will no longer have access to your trips.')) return;
    try { await tripsApi.removeBrowser(browser.id); setBrowsers((await tripsApi.browsers()).browsers); }
    catch (error) { setBrowserError(error instanceof Error ? error.message : String(error)); }
  };

  const unpair = async (phone: PairedPhone) => {
    if (!window.confirm(`Unpair ${phone.name}? The phone and this planner each keep a copy of the trips, and changes stop being shared.`)) return;
    try {
      await api.unpair(phone.id);
      track('phone-unpaired', { detail: { phones: (phones?.length ?? 1) - 1 } });
    } catch (error) {
      setUnpairError(error instanceof Error ? error.message : String(error));
    }
    void refresh();
  };

  const origin = pairing.status === 'ready' ? new URL(pairing.url).origin : window.location.origin;
  const local = /^https?:\/\/(localhost|127\.|\[::1\])/.test(origin);

  return (
    <div className="debug-backdrop" role="dialog" aria-modal="true" aria-label="Phone" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <article className="debug-dialog phone-dialog">
        <header className="debug-header">
          <div>
            <p className="eyebrow">Ride app</p>
            <h2>Phone</h2>
          </div>
          <div className="debug-header-actions">
            <button ref={closeButton} type="button" className="button button-outline" onClick={onClose}>Close <kbd>Esc</kbd></button>
          </div>
        </header>
        <div className="phone-body">
          <div className="phone-pairing">
            {pairing.status === 'ready'
              ? <div className="phone-qr" role="img" aria-label="Pairing code for the ride app" dangerouslySetInnerHTML={{ __html: pairing.svg }} />
              : <div className="phone-qr phone-qr-empty">
                {pairing.status === 'loading' ? 'Making a code…' : pairing.status === 'failed' ? pairing.message : justPaired ? 'Paired. This planner and the phone now share their trips.' : 'Pair your phone to share trips with it.'}
              </div>}
            <Button type="button" variant={pairing.status === 'ready' ? 'outline' : 'primary'} onClick={() => void pair()} disabled={pairing.status === 'loading'}>
              {pairing.status === 'ready' ? 'New code' : 'Pair a phone'}
            </Button>
          </div>
          <div className="phone-notes">
            <p>In the Haere app, choose <strong>Pair with the web planner</strong> and scan the code. It works once, for 10 minutes.</p>
            <p>Pairing puts the trips from this planner and from the phone together; nothing is deleted. After that both show the same trips, and a change on either side reaches the other within seconds while it is open. A trip being ridden is never changed mid-ride; it updates when the ride ends. Unpairing leaves each side a copy.</p>
            <p className="field-note">Place names appear on planning screens only. The riding map uses Start, Stop n and Finish.</p>
            {local ? <p className="field-note field-error">The code points at {origin}, which a phone cannot reach. Set RIDE_LINK_BASE_URL to an address of this computer the phone can reach (for example http://192.168.1.20:8787) and restart the API.</p> : null}
            <h3 className="phone-heading">Paired phones{live || listError ? null : <span className="field-note"> · connecting…</span>}</h3>
            {listError ?? unpairError ? <p className="field-note field-error">{listError ?? unpairError}</p> : null}
            {phones === undefined ? (listError ? null : <p className="field-note">Loading…</p>) : phones.length === 0 ? <p className="field-note">None yet.</p> : (
              <ul className="phone-list">
                {phones.map((phone) => (
                  <li key={phone.id}>
                    <div><strong><span className={phone.connected ? 'phone-dot phone-dot-live' : 'phone-dot'} aria-hidden="true" />{phone.name}</strong>
                      <span>{phone.downloading ? `Downloading ${tripName(phone.downloading)}…` : phone.connected ? 'Connected now' : `Last seen ${time(phone.lastSeenAt)}`} · paired {time(phone.pairedAt)}</span></div>
                    <Button type="button" variant="quiet" onClick={() => void unpair(phone)}>Unpair</Button>
                  </li>
                ))}
              </ul>
            )}
            <h3 className="phone-heading">Browsers</h3>
            {browserError ? <p className="field-note field-error">{browserError}</p> : null}
            {browsers ? <ul className="phone-list">{browsers.map((browser) => <li key={browser.id}><div><strong>{browser.current ? 'This browser' : 'Paired browser'}</strong><span>Added {time(browser.addedAt)} · last used {time(browser.lastUsedAt)}</span></div>{!browser.current ? <Button type="button" variant="quiet" onClick={() => void removeBrowser(browser)}>Remove</Button> : null}</li>)}</ul> : <p className="field-note">Loading…</p>}
          </div>
        </div>
      </article>
    </div>
  );
}
